import type { ComponentChildren, ComponentType, RefObject, VNode } from 'preact';
import type { LucideProps } from 'lucide-preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Popover, type Placement } from './Popover';
import { Icon } from '../primitives/Icon';
import { Kbd } from '../primitives/Kbd';
import { cn } from '@/lib/cn';

export interface MenuItem {
  id: string;
  label: ComponentChildren;
  icon?: ComponentType<LucideProps>;
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  href?: string;
}

export interface MenuSection {
  id: string;
  label?: string;
  items: MenuItem[];
}

export interface MenuProps {
  open: boolean;
  onClose: () => void;
  anchor: RefObject<HTMLElement>;
  sections?: MenuSection[];
  items?: MenuItem[];
  placement?: Placement;
  matchAnchorWidth?: boolean;
  /**
   * Item que arranca resaltado al abrir. Como abrir enfoca el item activo, el
   * navegador lo trae solo a la vista: en un menu largo que scrollea, sin
   * esto se abre por arriba y no se ve donde estas.
   */
  initialItemId?: string;
  className?: string;
  'aria-label'?: string;
}

export function Menu({
  open,
  onClose,
  anchor,
  sections,
  items,
  placement = 'bottom-start',
  matchAnchorWidth,
  initialItemId,
  className,
  'aria-label': ariaLabel = 'Menu',
}: MenuProps) {
  const finalSections: MenuSection[] = sections ?? (items ? [{ id: 'default', items }] : []);
  const allItems = finalSections.flatMap((s) => s.items.filter((i) => !i.disabled));
  const [activeIdx, setActiveIdx] = useState(0);
  const itemRefs = useRef<Record<string, HTMLButtonElement | HTMLAnchorElement | null>>({});

  useEffect(() => {
    if (!open) return;
    const idx = initialItemId ? allItems.findIndex((i) => i.id === initialItemId) : -1;
    setActiveIdx(idx >= 0 ? idx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialItemId]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % allItems.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + allItems.length) % allItems.length);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setActiveIdx(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setActiveIdx(allItems.length - 1);
      } else if (e.key === 'Enter' || e.key === ' ') {
        const item = allItems[activeIdx];
        if (item) {
          e.preventDefault();
          select(item);
        }
      } else if (e.key === 'Tab') {
        // Audit P2 #60: el Menu se mantenía abierto al tabular fuera y el
        // foco terminaba "perdido" detrás del overlay. Cerrar permite a
        // los AT (lectores de pantalla, keyboard-only) salir del menu de
        // forma natural sin tener que Escapear.
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allItems.length, activeIdx]);

  useEffect(() => {
    const item = allItems[activeIdx];
    if (!item) return;
    itemRefs.current[item.id]?.focus();
  }, [activeIdx, allItems]);

  function select(item: MenuItem) {
    if (item.disabled) return;
    item.onSelect?.();
    onClose();
  }

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchor={anchor}
      placement={placement}
      matchAnchorWidth={!!matchAnchorWidth}
      role="menu"
      aria-label={ariaLabel}
      className={cn('p-1 min-w-[180px] max-w-[320px]', className)}
    >
      {finalSections.map((section, si) => (
        <div key={section.id}>
          {section.label ? (
            <p className="px-2 pt-2 pb-1 text-3xs font-semibold uppercase tracking-wide text-fg-faint">
              {section.label}
            </p>
          ) : null}
          {section.items.map((item) => {
            const idx = allItems.indexOf(item);
            const active = idx === activeIdx;
            const className = cn(
              'flex w-full items-center gap-2.5 rounded text-left text-13 px-2 py-1.5 outline-none transition-colors duration-med',
              item.disabled && 'opacity-50 cursor-not-allowed',
              !item.disabled && 'hover:bg-surface-2 focus-visible:bg-surface-2 cursor-pointer',
              item.danger && 'text-error hover:bg-error-bg focus-visible:bg-error-bg',
              active && !item.disabled && 'bg-surface-2',
            );
            const inner = (
              <>
                {item.icon ? <Icon as={item.icon} size={14} className={item.danger ? 'text-error' : 'text-fg-muted'} /> : null}
                <span className="flex-1 truncate">{item.label}</span>
                {item.shortcut ? <Kbd>{item.shortcut}</Kbd> : null}
              </>
            );
            if (item.href) {
              return (
                <a
                  key={item.id}
                  ref={(el) => {
                    itemRefs.current[item.id] = el;
                  }}
                  href={item.href}
                  role="menuitem"
                  className={className}
                  onClick={() => select(item)}
                >
                  {inner}
                </a>
              );
            }
            return (
              <button
                key={item.id}
                ref={(el) => {
                  itemRefs.current[item.id] = el;
                }}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                className={className}
                onClick={() => select(item)}
              >
                {inner}
              </button>
            );
          })}
          {si < finalSections.length - 1 ? (
            <div className="my-1 h-px bg-line-subtle" aria-hidden="true" />
          ) : null}
        </div>
      ))}
    </Popover>
  );
}

export interface DropdownMenuTriggerProps {
  ref: RefObject<HTMLButtonElement>;
  onClick: () => void;
  'aria-expanded': boolean;
  'aria-haspopup': 'menu';
}

export interface DropdownMenuProps extends Omit<MenuProps, 'open' | 'onClose' | 'anchor'> {
  trigger: (props: DropdownMenuTriggerProps) => VNode;
}

export function DropdownMenu({ trigger, ...menuProps }: DropdownMenuProps) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      {trigger({
        ref: anchor,
        onClick: () => setOpen((p) => !p),
        'aria-expanded': open,
        'aria-haspopup': 'menu',
      })}
      <Menu
        {...menuProps}
        open={open}
        onClose={() => setOpen(false)}
        anchor={anchor}
      />
    </>
  );
}
